import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { X, ShieldCheck, ArrowRight, Check, Package, Globe, Users, Award, Handshake, Store, Building2, ShoppingBag, Sparkles } from 'lucide-react';

// ─── Obfuscated email ─────────────────────────────────────────────────────────
const emailParts = ['luxeme', 'japan', '@', 'gmail', '.com'];
const getEmail = () => emailParts.join('').replace('@gmail.com', '@gmail.com');

// ─── Flower verification challenge (Sakura = correct) ────────────────────────
const flowerChallenge = {
  question: "Which flower is the timeless symbol of Japan's spring season?",
  options: [
    { id: 1, label: "Sakura",  emoji: "🌸", correct: true  },
    { id: 2, label: "Lotus",   emoji: "🪷", correct: false },
    { id: 3, label: "Orchid",  emoji: "🌺", correct: false },
  ]
};

// ─── Partner types ────────────────────────────────────────────────────────────
const partnerTypes = [
  { icon: Globe,        label: "Exclusive Distributor",    desc: "Territory-wide distribution rights for one or more brands" },
  { icon: Store,        label: "Multi-brand Retailer",     desc: "Boutiques, concept stores, and specialty beauty retailers" },
  { icon: Building2,    label: "Department Store",         desc: "Prestigious department store beauty & lifestyle floors" },
  { icon: ShoppingBag,  label: "E-commerce Platform",      desc: "Online marketplaces and curated digital boutiques" },
  { icon: Users,        label: "Spa & Wellness Centre",    desc: "Luxury spas, wellness resorts, and treatment centres" },
  { icon: Handshake,    label: "Brand Agent / Rep",        desc: "Regional agents and brand representatives seeking new lines" },
];

// ─── What you access ──────────────────────────────────────────────────────────
const benefits = [
  {
    icon: Package,
    title: "Exclusive Wholesale Pricing",
    body: "Access competitive wholesale rates, transparent MOQ structures, and tiered pricing designed for both first-time buyers and established partners.",
  },
  {
    icon: Award,
    title: "Full Brand Support",
    body: "Receive complete brand training, product knowledge materials, digital assets, and co-marketing tools to launch and grow each brand in your market.",
  },
  {
    icon: Globe,
    title: "Territory Options",
    body: "Exclusive or semi-exclusive territory agreements are available for qualified distributors, protecting your investment and market positioning.",
  },
  {
    icon: Sparkles,
    title: "Direct Japan Connection",
    body: "Benefit from our direct relationships with Japanese brand founders — ensuring authentic storytelling, product accuracy, and first access to new launches.",
  },
];

// ─── Business type options for form ──────────────────────────────────────────
const businessTypeOptions = [
  "Exclusive Distributor",
  "Multi-brand Retailer",
  "Department Store / Luxury Retail",
  "E-commerce Platform",
  "Spa, Wellness Centre or Hotel",
  "Brand Agent / Representative",
  "Other",
];

const brandOptions = [
  "Waphyto", "Seichajo Yamashina", "Tamanohada", "Sophistance",
  "BelAge", "Grand Fond Blanc", "Fairydrops", "Lacasta",
  "Mesoins", "Arpa", "Intime Organics", "Loqzin", "All Brands",
];

// ─── Component ────────────────────────────────────────────────────────────────
const WholesalePage = () => {
  // Form state
  const [form, setForm] = useState({
    name: '', company: '', position: '', country: '',
    businessType: '', brandsInterest: [], message: '',
  });
  const [formError, setFormError] = useState('');

  // Verification state
  const [showVerif,  setShowVerif]  = useState(false);
  const [verifSel,   setVerifSel]   = useState(null);
  const [verifError, setVerifError] = useState(false);
  const [verified,   setVerified]   = useState(false);

  const setField = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setFormError('');
  };

  const toggleBrand = (brand) => {
    setForm(f => ({
      ...f,
      brandsInterest: f.brandsInterest.includes(brand)
        ? f.brandsInterest.filter(b => b !== brand)
        : [...f.brandsInterest, brand],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.country || !form.businessType) {
      setFormError('Please fill in all required fields.');
      return;
    }
    if (verified) {
      sendEmail();
    } else {
      setShowVerif(true);
      setVerifSel(null);
      setVerifError(false);
    }
  };

  const handleVerify = () => {
    if (!verifSel) return;
    const opt = flowerChallenge.options.find(o => o.id === verifSel);
    if (opt?.correct) {
      setVerified(true);
      setShowVerif(false);
      setTimeout(sendEmail, 300);
    } else {
      setVerifError(true);
      setVerifSel(null);
    }
  };

  const sendEmail = () => {
    const email = getEmail();
    const brands = form.brandsInterest.length > 0
      ? form.brandsInterest.join(', ')
      : 'Not specified';
    const subject = encodeURIComponent(`Wholesale Inquiry — ${form.company}`);
    const body = encodeURIComponent(
`Dear Luxe Me Japan Team,

I would like to inquire about wholesale partnership opportunities.

CONTACT INFORMATION
───────────────────
Contact Name : ${form.name}
Company      : ${form.company}
Position     : ${form.position || 'Not specified'}
Country      : ${form.country}

BUSINESS PROFILE
───────────────────
Business Type     : ${form.businessType}
Brands of Interest: ${brands}

MESSAGE
───────────────────
${form.message || 'No additional message provided.'}

I look forward to discussing this opportunity with your team.

Best regards,
${form.name}
${form.company}`
    );
    window.location.href = `mailto:${getEmail()}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-onyx">
      <Helmet>
        <title>Wholesale — Luxe Me Japan | B2B Partnership & Distribution</title>
        <meta name="description" content="Become a wholesale partner of Luxe Me Japan. Access exclusive pricing, distribution rights, and brand support for Japan's finest beauty and lifestyle brands." />
      </Helmet>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <header className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          {/* Replace wholesale-hero.jpg with your own image */}
          <img
            src="/images/wholesale-hero.jpg"
            className="w-full h-full object-cover brightness-50"
            alt="Wholesale Partnership"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-onyx/50 via-onyx/30 to-onyx"></div>
        </div>

        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none z-[1]">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-gold/6 blur-[150px] rounded-full"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto">
          <div className="inline-flex items-center gap-4 mb-6 sm:mb-8 opacity-0 animate-fade-up">
            <span className="w-10 h-px bg-gold/60"></span>
            <span className="text-gold tracking-[0.45em] text-[10px] sm:text-xs uppercase font-light">B2B &amp; Professional</span>
            <span className="w-10 h-px bg-gold/60"></span>
          </div>

          <h1 className="font-serif text-5xl sm:text-7xl md:text-8xl mb-6 sm:mb-8 leading-tight opacity-0 animate-fade-up stagger-1">
            <span className="text-gradient">Access</span>
            <span className="text-platinum"> the World</span>
            <br />
            <span className="text-platinum/80 text-3xl sm:text-4xl md:text-5xl font-light italic">of Japanese Excellence</span>
          </h1>

          <p className="text-platinum/65 font-light text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-10 sm:mb-14 opacity-0 animate-fade-up stagger-2">
            We are the exclusive bridge between Japan's most coveted beauty and lifestyle brands 
            and the world's finest retailers, distributors, and partners. 
            Your market. Their excellence. Our connection.
          </p>

          <div className="opacity-0 animate-fade-up stagger-3">
            <a
              href="#inquiry"
              className="inline-flex items-center gap-3 bg-gold text-onyx px-10 sm:px-14 py-4 sm:py-5 rounded-full font-bold tracking-widest uppercase text-xs hover:bg-white transition-all duration-500 shine-effect"
            >
              Apply for Partnership <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-40">
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex items-start justify-center pt-2">
            <div className="w-1 h-3 bg-gold rounded-full animate-pulse"></div>
          </div>
        </div>
      </header>

      {/* ── Introduction ──────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-12 bg-ebony">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Text */}
            <div className="space-y-7 sm:space-y-9">
              <div className="inline-flex items-center gap-4 text-gold tracking-[0.4em] text-xs uppercase">
                <span className="w-10 h-px bg-gold/60"></span> Our Mission
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight">
                Where Japan's<br />
                <span className="text-gold italic">Finest Meets</span><br />
                the World.
              </h2>
              <p className="text-platinum/65 leading-relaxed text-base sm:text-lg font-light">
                Luxe Me Japan was born from a singular conviction: Japan's extraordinary beauty and lifestyle brands deserve a global stage — and the world deserves to experience them. As a dedicated curatorial and distribution bridge, we represent only brands that meet the highest standards of craftsmanship, authenticity, and innovation.
              </p>
              <p className="text-platinum/50 leading-relaxed text-sm sm:text-base font-light">
                For our wholesale and distribution partners, this means access to a pre-vetted, genuinely exceptional portfolio — supported by our deep Japan expertise, brand relationships, and full commercial infrastructure. We don't simply connect; we co-build your success story with each brand we represent.
              </p>
              <div className="pt-4 grid grid-cols-3 gap-6 sm:gap-10 border-t border-gold/10 pt-8">
                <div>
                  <p className="font-serif text-3xl sm:text-4xl text-gold">12+</p>
                  <p className="text-[10px] sm:text-xs uppercase tracking-widest text-platinum/40 mt-1">Curated Brands</p>
                </div>
                <div>
                  <p className="font-serif text-3xl sm:text-4xl text-gold">20+</p>
                  <p className="text-[10px] sm:text-xs uppercase tracking-widest text-platinum/40 mt-1">Years Expertise</p>
                </div>
                <div>
                  <p className="font-serif text-3xl sm:text-4xl text-gold">B2B</p>
                  <p className="text-[10px] sm:text-xs uppercase tracking-widest text-platinum/40 mt-1">Worldwide</p>
                </div>
              </div>
            </div>

            {/* Image placeholder */}
            <div className="relative group">
              <div className="absolute -inset-4 border border-gold/20 rounded-2xl group-hover:border-gold/40 transition-all duration-700 hidden sm:block"></div>
              <div className="relative z-10 aspect-[4/5] rounded-xl overflow-hidden bg-ebony border border-white/10">
                <img
                  src="/images/wholesale-hero.jpg"
                  alt="Luxe Me Japan Brand Showcase"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-onyx/50 via-transparent to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Benefits ──────────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-32 px-4 sm:px-6 lg:px-12 bg-onyx">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14 sm:mb-20">
            <p className="text-gold tracking-[0.4em] text-xs uppercase mb-4">What You Gain</p>
            <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl mb-4">
              A Partnership Built on <span className="text-gold italic">Excellence</span>
            </h2>
            <div className="w-20 h-px bg-gold/30 mx-auto"></div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {benefits.map((b, i) => (
              <div
                key={i}
                className="group relative p-8 sm:p-10 rounded-2xl bg-ebony border border-white/5 hover:border-gold/30 transition-all duration-700 hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-full border border-gold/30 flex items-center justify-center mb-6 group-hover:bg-gold/10 transition-colors">
                  <b.icon className="w-7 h-7 text-gold" />
                </div>
                <h3 className="font-serif text-xl sm:text-2xl mb-3 text-platinum group-hover:text-gold transition-colors">
                  {b.title}
                </h3>
                <p className="text-platinum/50 text-sm leading-relaxed">{b.body}</p>
                <div className="mt-6 h-px w-0 group-hover:w-full bg-gradient-to-r from-gold/50 to-transparent transition-all duration-700"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Partner Types ─────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-12 bg-ebony">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16">
            <p className="text-gold tracking-[0.4em] text-xs uppercase mb-4">Who We Work With</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl mb-4">
              Built for <span className="text-gold italic">Professionals</span>
            </h2>
            <p className="text-platinum/50 max-w-xl mx-auto text-sm sm:text-base">
              Whether you are an exclusive distributor in Southeast Asia or a curated boutique in Europe, 
              we have a wholesale structure designed for your business model.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {partnerTypes.map((pt, i) => (
              <div
                key={i}
                className="group text-center p-5 sm:p-6 rounded-xl bg-onyx border border-white/5 hover:border-gold/30 transition-all duration-500"
              >
                <div className="w-12 h-12 mx-auto mb-4 rounded-full border border-gold/20 flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                  <pt.icon className="w-5 h-5 text-gold" />
                </div>
                <p className="font-serif text-sm sm:text-base text-platinum/80 group-hover:text-gold transition-colors leading-tight mb-2">
                  {pt.label}
                </p>
                <p className="text-platinum/35 text-xs leading-relaxed hidden sm:block">{pt.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Inquiry Form ──────────────────────────────────────────────────── */}
      <section id="inquiry" className="py-20 sm:py-32 px-4 sm:px-6 lg:px-12 relative overflow-hidden" style={{ background: '#f7f3ec' }}>
        {/* Subtle warm texture overlay */}
        <div className="absolute inset-0 pointer-events-none opacity-30"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(197,160,89,0.12) 0%, transparent 60%), radial-gradient(circle at 80% 80%, rgba(197,160,89,0.08) 0%, transparent 60%)' }}
        />

        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-4 tracking-[0.4em] text-xs uppercase mb-6" style={{ color: '#C5A059' }}>
              <span className="w-10 h-px" style={{ background: '#C5A059', opacity: 0.6 }}></span>
              Partnership Inquiry
              <span className="w-10 h-px" style={{ background: '#C5A059', opacity: 0.6 }}></span>
            </div>
            <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl mb-5" style={{ color: '#1a1a1a' }}>
              Begin Your <span className="italic" style={{ color: '#C5A059' }}>Journey</span>
            </h2>
            <p className="text-base sm:text-lg font-light max-w-2xl mx-auto leading-relaxed" style={{ color: '#555' }}>
              Share your details below. Our team will review your profile and reach out personally to discuss
              terms, pricing, and how we can build something exceptional together.
            </p>
          </div>

          {/* Form Card */}
          <div className="relative">
            {/* Outer glow rings */}
            <div className="absolute -inset-4 sm:-inset-6 rounded-3xl pointer-events-none" style={{ border: '1px solid rgba(197,160,89,0.25)' }}></div>
            <div className="absolute -inset-8 sm:-inset-10 rounded-3xl pointer-events-none hidden sm:block" style={{ border: '1px solid rgba(197,160,89,0.10)' }}></div>

            <form
              onSubmit={handleSubmit}
              className="relative rounded-2xl p-6 sm:p-10 lg:p-14 space-y-8"
              style={{ background: '#ffffff', border: '1px solid rgba(197,160,89,0.30)', boxShadow: '0 20px 60px rgba(0,0,0,0.10), 0 4px 16px rgba(197,160,89,0.08)' }}
              data-testid="wholesale-form"
            >
              {/* Corner decorations */}
              <div className="absolute top-4 left-4 w-7 h-7 rounded-tl-lg" style={{ borderTop: '2px solid rgba(197,160,89,0.50)', borderLeft: '2px solid rgba(197,160,89,0.50)' }}></div>
              <div className="absolute top-4 right-4 w-7 h-7 rounded-tr-lg" style={{ borderTop: '2px solid rgba(197,160,89,0.50)', borderRight: '2px solid rgba(197,160,89,0.50)' }}></div>
              <div className="absolute bottom-4 left-4 w-7 h-7 rounded-bl-lg" style={{ borderBottom: '2px solid rgba(197,160,89,0.50)', borderLeft: '2px solid rgba(197,160,89,0.50)' }}></div>
              <div className="absolute bottom-4 right-4 w-7 h-7 rounded-br-lg" style={{ borderBottom: '2px solid rgba(197,160,89,0.50)', borderRight: '2px solid rgba(197,160,89,0.50)' }}></div>

              {/* Row 1: Name + Company */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase mb-2 font-medium" style={{ color: '#C5A059' }}>
                    Contact Name <span style={{ color: '#C5A059' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setField('name', e.target.value)}
                    placeholder="Your full name"
                    data-testid="field-name"
                    className="w-full rounded-xl px-5 py-4 text-sm outline-none transition-all duration-300"
                    style={{ background: '#faf8f4', border: '1px solid rgba(197,160,89,0.30)', color: '#1a1a1a' }}
                    onFocus={e => e.target.style.border = '1px solid rgba(197,160,89,0.80)'}
                    onBlur={e => e.target.style.border = '1px solid rgba(197,160,89,0.30)'}
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase mb-2 font-medium" style={{ color: '#C5A059' }}>
                    Company / Organisation <span style={{ color: '#C5A059' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={e => setField('company', e.target.value)}
                    placeholder="Your company name"
                    data-testid="field-company"
                    className="w-full rounded-xl px-5 py-4 text-sm outline-none transition-all duration-300"
                    style={{ background: '#faf8f4', border: '1px solid rgba(197,160,89,0.30)', color: '#1a1a1a' }}
                    onFocus={e => e.target.style.border = '1px solid rgba(197,160,89,0.80)'}
                    onBlur={e => e.target.style.border = '1px solid rgba(197,160,89,0.30)'}
                  />
                </div>
              </div>

              {/* Row 2: Position + Country */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase mb-2 font-medium" style={{ color: '#C5A059' }}>
                    Position / Title
                  </label>
                  <input
                    type="text"
                    value={form.position}
                    onChange={e => setField('position', e.target.value)}
                    placeholder="e.g. CEO, Buyer, Director"
                    data-testid="field-position"
                    className="w-full rounded-xl px-5 py-4 text-sm outline-none transition-all duration-300"
                    style={{ background: '#faf8f4', border: '1px solid rgba(197,160,89,0.30)', color: '#1a1a1a' }}
                    onFocus={e => e.target.style.border = '1px solid rgba(197,160,89,0.80)'}
                    onBlur={e => e.target.style.border = '1px solid rgba(197,160,89,0.30)'}
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase mb-2 font-medium" style={{ color: '#C5A059' }}>
                    Country / Region <span style={{ color: '#C5A059' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={e => setField('country', e.target.value)}
                    placeholder="e.g. Malaysia, France, UAE"
                    data-testid="field-country"
                    className="w-full rounded-xl px-5 py-4 text-sm outline-none transition-all duration-300"
                    style={{ background: '#faf8f4', border: '1px solid rgba(197,160,89,0.30)', color: '#1a1a1a' }}
                    onFocus={e => e.target.style.border = '1px solid rgba(197,160,89,0.80)'}
                    onBlur={e => e.target.style.border = '1px solid rgba(197,160,89,0.30)'}
                  />
                </div>
              </div>

              {/* Row 3: Business Type */}
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase mb-3 font-medium" style={{ color: '#C5A059' }}>
                  Business Type <span style={{ color: '#C5A059' }}>*</span>
                </label>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {businessTypeOptions.map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setField('businessType', opt)}
                      className="px-4 py-2.5 rounded-full text-xs tracking-wide transition-all duration-300"
                      style={form.businessType === opt
                        ? { background: '#C5A059', color: '#fff', border: '1px solid #C5A059', fontWeight: 600 }
                        : { background: 'transparent', color: '#555', border: '1px solid rgba(197,160,89,0.35)' }
                      }
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Row 4: Brands of Interest */}
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase mb-3 font-medium" style={{ color: '#C5A059' }}>
                  Brands of Interest
                  <span className="ml-3 normal-case tracking-normal text-[9px]" style={{ color: '#999' }}>Select all that apply</span>
                </label>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {brandOptions.map(b => {
                    const sel = form.brandsInterest.includes(b);
                    return (
                      <button
                        key={b}
                        type="button"
                        onClick={() => toggleBrand(b)}
                        className="flex items-center gap-1.5 px-4 py-2.5 rounded-full text-xs tracking-wide transition-all duration-300"
                        style={sel
                          ? { background: 'rgba(197,160,89,0.15)', color: '#C5A059', border: '1px solid rgba(197,160,89,0.60)' }
                          : { background: 'transparent', color: '#666', border: '1px solid rgba(197,160,89,0.25)' }
                        }
                      >
                        {sel && <Check className="w-3 h-3" />}
                        {b}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Row 5: Message */}
              <div>
                <label className="block text-[10px] tracking-[0.3em] uppercase mb-2 font-medium" style={{ color: '#C5A059' }}>
                  Additional Information
                  <span className="ml-3 normal-case tracking-normal text-[9px]" style={{ color: '#999' }}>Tell us about your business and goals</span>
                </label>
                <textarea
                  value={form.message}
                  onChange={e => setField('message', e.target.value)}
                  rows={5}
                  placeholder="Describe your company, your market, current portfolio, and what you are looking to achieve with Japanese brands..."
                  data-testid="field-message"
                  className="w-full rounded-xl px-5 py-4 text-sm outline-none transition-all duration-300 resize-none"
                  style={{ background: '#faf8f4', border: '1px solid rgba(197,160,89,0.30)', color: '#1a1a1a' }}
                  onFocus={e => e.target.style.border = '1px solid rgba(197,160,89,0.80)'}
                  onBlur={e => e.target.style.border = '1px solid rgba(197,160,89,0.30)'}
                />
              </div>

              {/* Error */}
              {formError && (
                <p className="text-sm text-center" style={{ color: '#c0392b' }}>{formError}</p>
              )}

              {/* Submit */}
              <div className="text-center pt-2">
                <button
                  type="submit"
                  data-testid="wholesale-submit"
                  className={`group inline-flex items-center gap-3 px-12 sm:px-16 py-4 sm:py-5 rounded-full font-bold tracking-widest uppercase text-xs transition-all duration-500 shine-effect ${
                    verified ? 'bg-green-700 text-white' : 'bg-gold text-onyx hover:bg-white'
                  }`}
                >
                  {verified ? (
                    <><Check className="w-5 h-5" /> Inquiry Sent</>
                  ) : (
                    <><ShieldCheck className="w-5 h-5 group-hover:rotate-12 transition-transform" /> Send Inquiry</>
                  )}
                </button>
                <p className="mt-5 text-xs flex items-center justify-center gap-2" style={{ color: '#aaa' }}>
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Protected form — human verification required before sending
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* ── Bottom tagline ────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-12 bg-ebony border-t border-gold/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-platinum/40 text-sm sm:text-base tracking-wide mb-3 font-light">
            All partnerships are reviewed on a case-by-case basis.
          </p>
          <h3 className="font-serif text-xl sm:text-2xl text-platinum mb-1">
            Luxe Me Japan — Curation of Excellence
          </h3>
          <p className="text-gold tracking-widest text-xs uppercase">
            Bridging Japan &nbsp;·&nbsp; Connecting the World
          </p>
        </div>
      </section>

      {/* ── Flower Verification Modal ─────────────────────────────────────── */}
      {showVerif && (
        <div
          className="fixed inset-0 z-[100] bg-onyx/96 backdrop-blur-xl flex items-center justify-center p-4"
          onClick={() => setShowVerif(false)}
          data-testid="verif-modal"
        >
          <div
            className="bg-ebony rounded-3xl max-w-md w-full border border-gold/20 shadow-2xl shadow-gold/10 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="bg-gradient-to-r from-gold/10 to-transparent p-6 border-b border-gold/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-6 h-6 text-gold" />
                <h3 className="font-serif text-xl text-platinum">One quick check</h3>
              </div>
              <button onClick={() => setShowVerif(false)} className="text-platinum/40 hover:text-gold transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-7 sm:p-9">
              <p className="text-platinum/60 text-center text-sm mb-6 leading-relaxed">
                To ensure we only receive genuine inquiries, please answer the following.
              </p>
              <p className="text-gold text-center font-serif text-lg mb-7">
                {flowerChallenge.question}
              </p>

              <div className="grid grid-cols-3 gap-4 mb-6">
                {flowerChallenge.options.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => { setVerifSel(opt.id); setVerifError(false); }}
                    className={`flex flex-col items-center gap-2 p-5 rounded-xl border transition-all duration-300 ${
                      verifSel === opt.id
                        ? 'border-gold bg-gold/10 scale-105'
                        : 'border-white/10 hover:border-gold/40'
                    }`}
                  >
                    <span className="text-4xl">{opt.emoji}</span>
                    <span className="text-xs text-platinum/65 font-light">{opt.label}</span>
                  </button>
                ))}
              </div>

              {verifError && (
                <p className="text-red-400 text-sm text-center mb-5">
                  Hmm, that is not quite right. Try again.
                </p>
              )}

              <button
                onClick={handleVerify}
                disabled={!verifSel}
                data-testid="verif-confirm"
                className={`w-full py-4 rounded-full font-bold tracking-widest uppercase text-xs transition-all duration-500 ${
                  verifSel
                    ? 'bg-gold text-onyx hover:bg-white'
                    : 'bg-platinum/10 text-platinum/30 cursor-not-allowed'
                }`}
              >
                Confirm &amp; Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesalePage;
